import { ServaChartTypes, ServaChartOptions } from '../Interfaces/Chart/Type';
import { DataPoint } from '../Interfaces/Chart/USMap/DataPoint';
import ChartJSChartData from '../Interfaces/Responses/Reports/Charts/ChartData';
import ServaChart from '../Interfaces/Responses/Reports/Charts/Chart';

export const getServaChartOptionsFromServaChartType = (
    chartType: number,
): ServaChartOptions => {
    
    let sco: ServaChartOptions = {
        type: undefined,
        vertical: false,
        multiaxis: false,
        showLegend: false,
        fill: false,
    };
    
    switch(chartType)
    {
        case 1: // area
            sco.type = ServaChartTypes.area;
            sco.vertical = true;
            sco.multiaxis = false;
            sco.showLegend = true;
            sco.fill = true;
            break;
        case 2: // column
            sco.type = ServaChartTypes.column;
            sco.vertical = true;
            sco.multiaxis = true;
            sco.showLegend = true;
            sco.fill = false;
            break;
        case 3: // line
            sco.type = ServaChartTypes.line;
            sco.vertical = true;
            sco.multiaxis = true;
            sco.showLegend = true;
            sco.fill = false;
            break;
        case 4: // stepLine
            sco.type = ServaChartTypes.stepLine;
            sco.vertical = true;
            sco.multiaxis = true;
            sco.showLegend = true;
            sco.fill = false;
            // stepped = true in dataset
            break;
        case 5: // spline
            sco.type = ServaChartTypes.spline;
            sco.vertical = true;
            sco.multiaxis = true;
            sco.showLegend = true;
            sco.fill = false;
            // tension = 0.0 - 1.0 in dataset
            break;
        case 6: // stepArea
            sco.type = ServaChartTypes.stepArea;
            sco.vertical = true;
            sco.multiaxis = false;
            sco.showLegend = true;
            sco.fill = true;
            // fill = value in dataset
            // filler plug-in
            break;
        case 7: // splineArea
            sco.type = ServaChartTypes.splineArea;
            sco.vertical = true;
            sco.multiaxis = true;
            sco.showLegend = true;
            sco.fill = false;
            // tension = 0.0 - 1.0 in dataset
            break;
        case 8: // bar
            sco.type = ServaChartTypes.bar;
            sco.vertical = false;
            sco.multiaxis = false;
            sco.showLegend = true;
            sco.fill = false;
            break;
        case 9: // pie
            sco.type = ServaChartTypes.pie;
            sco.vertical = true;
            sco.multiaxis = false;
            sco.showLegend = true;
            sco.fill = false;
            break;
        case 10: // pyramid
            sco.type = ServaChartTypes.pyramid;
            break;
        case 11: // doughnut
            sco.type = ServaChartTypes.doughnut;
            sco.vertical = true;
            sco.multiaxis = false;
            sco.showLegend = true;
            sco.fill = false;
            break;
        case 12: // stacked column
            sco.type = ServaChartTypes.stackedColumn;
            sco.vertical = true;
            sco.multiaxis = true;
            sco.showLegend = true;
            sco.fill = false;
            break;
        case 13: // stacked bar
            sco.type = ServaChartTypes.stackedBar;
            sco.vertical = false;
            sco.multiaxis = false;
            sco.showLegend = true;
            sco.fill = false;
            break;
        case 14: // US map
            sco.type = ServaChartTypes.usmap;
            sco.vertical = true;
            sco.multiaxis = true;
            sco.showLegend = true;
            sco.fill = false;
            break;    
    }

    return sco;
};

export const transformServaChartToChartJSChart = (
    servaChart: ServaChart,
    servaChartOptions: ServaChartOptions,
    title: string
): ChartJSChartData => {
    let dataSetXAxis = undefined;
    let dataSetYAxis = undefined;
    let datasets = undefined;
    let labels = undefined;
    let rawVals = undefined;

    switch (servaChart.dataSets[0].canvasType) {
        case 'usmapStates':
            const dataPoints: DataPoint[] = [];
            for (let i = 0; i < servaChart.dataSets[0].rawVals.length; i++) {
                const dataPoint: DataPoint = {
                    label: servaChart.dataSets[0].rawVals[i],
                    y: Number.parseInt(servaChart.dataSets[1].rawVals[i]),
                };
                dataPoints.push(dataPoint);
            }
            return {
                labels: [],
                rawVals: [],
                datasets: [],
                title,
                dataPoints: dataPoints,
                isMap: true,
            };

        case 'pie':
        case 'doughnut':
            dataSetXAxis = servaChart.dataSets.filter(
                (eachDataSet) => eachDataSet.axis === 'X'
            );
            dataSetYAxis = servaChart.dataSets.filter(
                (eachDataSet) => eachDataSet.axis === 'Y'
            );
            datasets = dataSetYAxis.map((eachDataSet, index) => ({
                type: translateServaDatasetChartTypeToChartJSChartType(eachDataSet.canvasType),
                label: servaChartOptions.showLegend ? eachDataSet.label : '',
                fill: servaChartOptions.fill,
                drilldownDataSetInputId:
                    eachDataSet.drilldowns?.length > 0
                        ? eachDataSet.drilldowns[0].targetDataSetID
                        : 0,
                drilldownDataSetChartId:
                    eachDataSet.drilldowns?.length > 0
                        ? eachDataSet.drilldowns[0].targetChartID
                        : 0,
                drilldownTenantId:
                    eachDataSet.drilldowns?.length > 0
                        ? eachDataSet.drilldowns[0].tenantID
                        : 0,
                drilldowns: eachDataSet.drilldowns,
                data: eachDataSet.vals,
            }));
            labels = dataSetXAxis[0].vals;
            rawVals = dataSetXAxis[0].rawVals;
            return {
                labels,
                rawVals,
                datasets,
                title,
                isMap: false,
                dataPointLabel: servaChart.dataPointLabel
            };

        default:
            dataSetXAxis = servaChart.dataSets.filter(
                (eachDataSet) => eachDataSet.axis === 'X'
            );
            dataSetYAxis = servaChart.dataSets.filter(
                (eachDataSet) => eachDataSet.axis === 'Y'
            );
            datasets = dataSetYAxis.map((eachDataSet, index) => ({
                type: translateServaDatasetChartTypeToChartJSChartType(eachDataSet.canvasType),
                label: servaChartOptions.showLegend ? eachDataSet.label : '',
                fill: servaChartOptions.fill,
                stack: translateServaChartTypeToChartJSStackNumber(eachDataSet.reportChartType, index),
                drilldownDataSetInputId:
                    eachDataSet.drilldowns?.length > 0
                        ? eachDataSet.drilldowns[0].targetDataSetID
                        : 0,
                drilldownDataSetChartId:
                    eachDataSet.drilldowns?.length > 0
                        ? eachDataSet.drilldowns[0].targetChartID
                        : 0,
                drilldownTenantId:
                    eachDataSet.drilldowns?.length > 0
                        ? eachDataSet.drilldowns[0].tenantID
                        : 0,
                drilldowns: eachDataSet.drilldowns,
                data: eachDataSet.vals,
            }));
            labels = dataSetXAxis[0].vals;
            rawVals = dataSetXAxis[0].rawVals;
            return {
                labels,
                rawVals,
                datasets,
                title,
                isMap: false,
                dataPointLabel: servaChart.dataPointLabel
            };
    }
    /*if (servaChart.dataSets[0].canvasType === 'usmapStates') {
        const dataPoints: DataPoint[] = [];
        for (let i = 0; i < servaChart.dataSets[0].rawVals.length; i++) {
            const dataPoint: DataPoint = {
                label: servaChart.dataSets[0].rawVals[i],
                y: Number.parseInt(servaChart.dataSets[1].rawVals[i]),
            };
            dataPoints.push(dataPoint);
        }
        return {
            labels: [],
            rawVals: [],
            datasets: [],
            title,
            dataPoints: dataPoints,
            isMap: true,
        };
    } else {
        const dataSetXAxis = servaChart.dataSets.filter(
            (eachDataSet) => eachDataSet.axis === 'X'
        );
        const dataSetYAxis = servaChart.dataSets.filter(
            (eachDataSet) => eachDataSet.axis === 'Y'
        );
        const datasets: ChartDataset[] = dataSetYAxis.map((eachDataSet, index) => ({
            type: translateServaDatasetChartTypeToChartJSChartType(eachDataSet.canvasType),
            label: servaChartOptions.showLegend ? eachDataSet.label : '',
            fill: servaChartOptions.fill,
            stack: translateServaChartTypeToChartJSStackNumber(eachDataSet.reportChartType, index),
            drilldownDataSetInputId:
                eachDataSet.drilldowns?.length > 0
                    ? eachDataSet.drilldowns[0].targetDataSetID
                    : 0,
            drilldownDataSetChartId:
                eachDataSet.drilldowns?.length > 0
                    ? eachDataSet.drilldowns[0].targetChartID
                    : 0,
            drilldownTenantId:
                eachDataSet.drilldowns?.length > 0
                    ? eachDataSet.drilldowns[0].tenantID
                    : 0,
            
            data: eachDataSet.vals,
        }));
        const labels = dataSetXAxis[0].vals;
        const rawVals = dataSetXAxis[0].rawVals;
        return {
            labels,
            rawVals,
            datasets,
            title,
            isMap: false,
            dataPointLabel: servaChart.dataPointLabel
        };*/
    //};
};

export const translateServaDatasetChartTypeToChartJSChartType = (
    servaDatasetChartType: string
): string => {
    let x = servaDatasetChartType;

    switch (x) {
        case 'area':
            x = 'line';
            break;
        case 'column':
            x = 'bar';
            break;
        case 'line':
            x = 'line';
            break;
        case 'stepLine':
            x = 'line';
            break;
        case 'spline':
            x = 'line';
            break;
        case 'stepArea':
            x = 'line';
            break;
        case 'splineArea':
            x = 'line';
            break;
        case 'bar':
            x = 'bar';
            break;
        case 'pie':
            x = 'pie';
            break;
        case 'doughnut':
            x = 'doughnut';
            break;
        case 'stackedColumn':
            x = 'bar';
            break;
        case 'stackedBar':
            x = 'bar';
            break;
    }

    return x;
};

export const translateServaChartTypeToChartJSChartType = (
    servaChartOptions: ServaChartOptions
): string => {
    let x = ServaChartTypes[servaChartOptions.type ?? 0];

    switch (x) {
        case 'area':
            x = 'line';
            break;
        case 'column':
            x = 'bar';
            break;
        case 'line':
            x = 'line';
            break;
        case 'stepLine':
            x = 'line';
            break;
        case 'spline':
            x = 'line';
            break;
        case 'stepArea':
            x = 'line';
            break;
        case 'splineArea':
            x = 'line';
            break;
        case 'bar':
            x = 'bar';
            break;
        case 'pie':
            x = 'pie';
            break;
        case 'doughnut':
            x = 'doughnut';
            break;
        case 'stackedColumn':
            x = 'bar';
            break;
        case 'stackedBar':
            x = 'bar';
            break;
    }

    return x;
};

export const translateServaChartTypeToChartJSStackNumber = (
    reportChartType: number,
    index: number
): string => {
    let x = ServaChartTypes[reportChartType];
    
    ++index;
    
    switch (x)
    {
        case 'area':
            x = '1' + index.toString();
            break;
        case 'column':
            x = '2' + index.toString();
            break;
        case 'line':
            x = '3' + index.toString();
            break;
        case 'stepLine':
            x = '4' + index.toString();
            break;
        case 'spline':
            x = '5' + index.toString();
            break;
        case 'stepArea':
            x = '6' + index.toString();
            break;
        case 'splineArea':
            x = '7' + index.toString();
            break;
        case 'bar':
            x = '8' + index.toString();
            break;
        case 'pie':
            x = '9' + index.toString();
            break;
        case 'pyramid':
            x = '10' + index.toString();
            break;
        case 'doughnut':
            x = '11' + index.toString();
            break;
        case 'stackedColumn':
            x = '12';
            break;
        case 'stackedBar':
            x = '13';
            break;
        }
    return x;
};

export const translateServaChartisStackedToChartJSisStacked = (
    servaChartOptions: ServaChartOptions
): boolean => {
    let x = false;

    switch (servaChartOptions.type)
    {
        case ServaChartTypes.stackedColumn:
            x = true;
            break;
        case ServaChartTypes.stackedBar:
            x = true;
            break;
        }
    return x;
};
